import React, { useEffect, useState } from 'react';
import {
	ProjectState,
	EntityWithValueState,
	InputMapState,
	getParentCanvasEntity,
	ChangeSet,
	IValueStoreClient,
	StateMutationAction,
	LiteralValueState,
	DynamicValueTypes
} from '@elyx-code/project-logic-tree';
import { handleLiteralValueChange } from '../../services/execution/value-resolution';
import Button from '../../lib/button/Button';
import SmallDialog from '../../lib/dialog/SmallDialog';
import JsonEditorDialog from './JsonEditorDialog';
import {
	Box,
	TextField,
	Typography,
	IconButton,
	Tooltip,
	Menu,
	MenuItem,
	Tabs,
	Tab,
	Switch
} from '@mui/material';
import { EditorService } from '../../services/editor';
import InitializeValueButton from '../dynamic-value/InitializeValueButton';

export interface ISpreadsheetRowsBuilderDialogProps {
	project: EditorService;
	valueOwner: EntityWithValueState;
	onlyDefault?: boolean;
	value?: LiteralValueState;
	valueType?: DynamicValueTypes;
	onClose?: (e: React.MouseEvent | null) => void;
}

const TABS = ['Visual', 'JSON'];

const SpreadsheetRowsBuilderDialog: React.FC<
	ISpreadsheetRowsBuilderDialogProps
> = (props) => {
	const onlyDefault = props.onlyDefault || false;
	const [activeTab, setActiveTab] = useState(0);

	// Use props.value?.value for the initial state if passed
	let initValue = (
		Array.isArray(props.value?.value)
			? props.value.value
			: Array.isArray(props.value)
				? props.value
				: []
	) as any[][];
	if (initValue.length === 0) initValue = [[null]];
	const [rowsValue, setRowsValue] = useState<any[][]>(initValue);

	// Clone dialog state
	const [cloneDialogState, setCloneDialogState] = useState<{
		open: boolean;
		rowIndex: number | null;
	}>({ open: false, rowIndex: null });
	const [cloneCount, setCloneCount] = useState<number>(1);

	// Data type selector state
	const [typeMenuAnchor, setTypeMenuAnchor] = useState<null | HTMLElement>(
		null
	);
	const [activeCellForType, setActiveCellForType] = useState<{
		rIdx: number;
		cIdx: number;
	} | null>(null);

	const handleSave = () => {
		let storage: ChangeSet | IValueStoreClient =
			props.project.localTestValues;

		if (onlyDefault) {
			storage = props.project.logic.addChangeSet(
				new ChangeSet(
					props.project.logic,
					ProjectState.sessionAuthor,
					new Date().toISOString(),
					props.valueOwner,
					false,
					StateMutationAction.SetPrimitiveDefaultValue
				)
			);
		}

		const changeSet = handleLiteralValueChange(
			rowsValue,
			props.valueOwner,
			props.project.logic,
			storage,
			{
				forVarInst: true
			}
		);

		if (!!changeSet) {
			props.project.renderAndCloseChangeSet(changeSet);
		}

		const valueOwnerCanvasParentEntity = getParentCanvasEntity(
			props.valueOwner
		);

		if (valueOwnerCanvasParentEntity) {
			props.project.emit(valueOwnerCanvasParentEntity.id, {});
		}

		props.project.test?.validate();
		props.onClose?.(null);
	};

	const addRow = (index: number) => {
		const colsCount = rowsValue.length > 0 ? rowsValue[0].length : 1;
		const newRows = [...rowsValue];
		newRows.splice(index, 0, new Array(colsCount).fill(null));
		setRowsValue(newRows);
	};

	const addColumn = (index: number) => {
		let newRows = rowsValue.map((r) => {
			const newRow = [...r];
			newRow.splice(index, 0, null);
			return newRow;
		});
		if (newRows.length === 0) {
			newRows = [[null]];
		}
		setRowsValue(newRows);
	};

	const updateCell = (rIdx: number, cIdx: number, val: any) => {
		const newRows = [...rowsValue];
		newRows[rIdx] = [...newRows[rIdx]];
		newRows[rIdx][cIdx] = val;
		setRowsValue(newRows);
	};

	const removeRow = (rIdx: number) => {
		const next = rowsValue.filter((_, i) => i !== rIdx);
		if (next.length === 0) setRowsValue([[null]]);
		else setRowsValue(next);
	};

	const removeColumn = (cIdx: number) => {
		const next = rowsValue.map((r) => r.filter((_, i) => i !== cIdx));
		if (next.length === 0 || next[0].length === 0) setRowsValue([[null]]);
		else setRowsValue(next);
	};

	const handleCloneConfirm = () => {
		if (
			cloneDialogState.rowIndex === null ||
			isNaN(cloneCount) ||
			cloneCount <= 0
		)
			return;
		const rIdx = cloneDialogState.rowIndex;
		const rowToClone = [...rowsValue[rIdx]];
		const clonedRows = new Array(cloneCount)
			.fill(null)
			.map(() => [...rowToClone]);

		const newRows = [...rowsValue];
		newRows.splice(rIdx + 1, 0, ...clonedRows);
		setRowsValue(newRows);
		setCloneDialogState({ open: false, rowIndex: null });
	};

	const handleAutoFill = async () => {
		if ((props.valueOwner as any).codeNativeInitExampleValue) {
			try {
				const example = await (
					props.valueOwner as InputMapState
				).codeNativeInitExampleValue(props.valueOwner as any);
				if (example?.value && Array.isArray(example.value)) {
					setRowsValue(example.value);
				}
			} catch (e) {
				console.error('Failed to auto fill example', e);
			}
		}
	};

	const isEmptyPlaceholder =
		rowsValue.length === 1 &&
		rowsValue[0].length === 1 &&
		rowsValue[0][0] === null;

	const canAutoFill =
		isEmptyPlaceholder &&
		typeof (props.valueOwner as any).codeNativeInitExampleValue ===
			'function';

	const colsCount = rowsValue.length > 0 ? rowsValue[0].length : 0;

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				gap: '4px',
				paddingTop: '40px'
			}}
		>
			<span style={{ padding: '0 40px' }}>
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						gap: '16px'
					}}
				>
					<button
						className="dialog_back_button"
						onClick={(e: any) => props.onClose?.(e)}
					>
						<i className="fa-solid fa-arrow-left"></i>
					</button>
					<h1
						style={{
							margin: '0px',
							fontWeight: 500,
							fontSize: '28px'
						}}
					>
						Spreadsheet Rows Builder
					</h1>
				</div>
			</span>

			<Box flex={1} overflow="auto">
				<Box
					sx={{
						borderBottom: 1,
						borderColor: 'divider',
						padding: '0 40px'
					}}
				>
					<Tabs
						value={activeTab}
						onChange={(e, val) => setActiveTab(val)}
					>
						{TABS.map((tab, idx) => (
							<Tab key={idx} label={tab} />
						))}
					</Tabs>
				</Box>

				{activeTab === 0 && (
					<Box
						sx={{
							padding: '20px 40px',
							height: '100%',
							boxSizing: 'border-box'
						}}
					>
						<Box
							display="flex"
							justifyContent="flex-end"
							alignItems="center"
							mb={2}
						>
							<InitializeValueButton
								valueOwner={props.valueOwner}
								project={props.project}
								onInitializeValue={(val) => {
									if (Array.isArray(val)) {
										setRowsValue(val);
									}
								}}
							/>
						</Box>

						{rowsValue.length === 0 ? (
							<Typography color="textSecondary" variant="body2">
								No rows yet. Add some to get started.
							</Typography>
						) : (
							<Box
								display="flex"
								flexDirection="column"
								gap={1}
								overflow="auto"
								maxWidth="100%"
							>
								{/* Columns Header */}
								<Box
									display="flex"
									alignItems="center"
									gap={1}
									width="fit-content"
									minWidth="100%"
								>
									<Box
										width="48px"
										minWidth="48px"
										flexShrink={0}
									/>
									{Array.from({ length: colsCount }).map(
										(_, cIdx) => (
											<Box
												key={`col-${cIdx}`}
												flex="1 1 200px"
												minWidth="140px"
												maxWidth="240px"
												display="flex"
												alignItems="center"
												justifyContent="space-between"
												p={1}
												bgcolor="#f1f5f9"
												borderRadius={1}
												boxSizing="border-box"
											>
												<Typography
													variant="caption"
													fontWeight="bold"
												>
													Col {cIdx + 1}
												</Typography>
												<Box display="flex" gap={0.5}>
													{!isEmptyPlaceholder && (
														<Tooltip title="Insert column before">
															<IconButton
																size="small"
																onClick={() =>
																	addColumn(
																		cIdx
																	)
																}
															>
																<i
																	className="fa-solid fa-plus"
																	style={{
																		fontSize:
																			'10px'
																	}}
																></i>
															</IconButton>
														</Tooltip>
													)}
													{!isEmptyPlaceholder && (
														<Tooltip title="Delete column">
															<IconButton
																size="small"
																onClick={() =>
																	removeColumn(
																		cIdx
																	)
																}
															>
																<i
																	className="fa-light fa-trash"
																	style={{
																		fontSize:
																			'10px'
																	}}
																></i>
															</IconButton>
														</Tooltip>
													)}
												</Box>
											</Box>
										)
									)}
									<Box
										width="64px"
										minWidth="64px"
										flexShrink={0}
										display="flex"
										alignItems="center"
										gap={0.5}
									>
										<Tooltip title="Add column to end">
											<IconButton
												size="small"
												onClick={() =>
													addColumn(colsCount)
												}
											>
												<i
													className="fa-solid fa-plus"
													style={{
														fontSize: '10px',
														width: '12px'
													}}
												></i>
											</IconButton>
										</Tooltip>
									</Box>
								</Box>

								{/* Rows */}
								{rowsValue.map((row, rIdx) => (
									<Box
										key={`row-${rIdx}`}
										display="flex"
										alignItems="stretch"
										gap={1}
										width="fit-content"
										minWidth="100%"
									>
										<Box
											display="flex"
											flexDirection="column"
											width="48px"
											minWidth="48px"
											flexShrink={0}
											alignItems="center"
											justifyContent="center"
										>
											{!isEmptyPlaceholder && (
												<Tooltip title="Insert row before">
													<IconButton
														size="small"
														onClick={() =>
															addRow(rIdx)
														}
													>
														<i
															className="fa-solid fa-plus"
															style={{
																fontSize: '10px'
															}}
														></i>
													</IconButton>
												</Tooltip>
											)}
										</Box>

										{row.map((cell: any, cIdx: number) => {
											let icon = 'fa-font'; // string
											if (typeof cell === 'number')
												icon = 'fa-hashtag';
											else if (typeof cell === 'boolean')
												icon = 'fa-toggle-on';
											else if (cell === null)
												icon = 'fa-ban';

											return (
												<Box
													key={`cell-${rIdx}-${cIdx}`}
													flex="1 1 200px"
													minWidth="140px"
													maxWidth="240px"
													display="flex"
													alignItems="center"
													gap={1}
													p={1}
													border="1px solid #e2e8f0"
													borderRadius={1}
													boxSizing="border-box"
												>
													<IconButton
														size="small"
														onClick={(e) => {
															setTypeMenuAnchor(
																e.currentTarget
															);
															setActiveCellForType(
																{ rIdx, cIdx }
															);
														}}
													>
														<i
															className={`fa-solid ${icon}`}
															style={{
																fontSize:
																	'12px',
																color: '#64748b'
															}}
														></i>
													</IconButton>
													{cell === null ? (
														<Typography
															variant="body2"
															color="textSecondary"
															sx={{ flex: 1 }}
														>
															Empty
														</Typography>
													) : typeof cell ===
													  'boolean' ? (
														<Box
															display="flex"
															alignItems="center"
															flex={1}
														>
															<Switch
																size="small"
																checked={cell}
																onChange={(e) =>
																	updateCell(
																		rIdx,
																		cIdx,
																		e.target
																			.checked
																	)
																}
															/>
															<Typography
																variant="body2"
																color="textPrimary"
															>
																{cell
																	? 'true'
																	: 'false'}
															</Typography>
														</Box>
													) : (
														<TextField
															fullWidth
															size="small"
															multiline={
																typeof cell ===
																'string'
															}
															sx={{
																'& .MuiInputBase-root':
																	{ py: 0 },
																'& .MuiInputBase-input':
																	{ py: 0.25 }
															}}
															value={
																cell === null
																	? ''
																	: String(
																			cell
																		)
															}
															onChange={(e) => {
																const strVal =
																	e.target
																		.value;
																if (
																	typeof cell ===
																	'number'
																)
																	updateCell(
																		rIdx,
																		cIdx,
																		Number(
																			strVal
																		) || 0
																	);
																else if (
																	typeof cell ===
																	'boolean'
																)
																	updateCell(
																		rIdx,
																		cIdx,
																		strVal ===
																			'true'
																	);
																else
																	updateCell(
																		rIdx,
																		cIdx,
																		strVal
																	);
															}}
															variant="standard"
															InputProps={{
																disableUnderline: true
															}}
														/>
													)}
												</Box>
											);
										})}

										<Box
											width="64px"
											minWidth="64px"
											flexShrink={0}
											display="flex"
											alignItems="center"
											gap={0.5}
										>
											{!isEmptyPlaceholder && (
												<>
													<Tooltip title="Clone row">
														<IconButton
															size="small"
															onClick={() =>
																setCloneDialogState(
																	{
																		open: true,
																		rowIndex:
																			rIdx
																	}
																)
															}
														>
															<i
																className="fa-light fa-copy"
																style={{
																	fontSize:
																		'12px'
																}}
															></i>
														</IconButton>
													</Tooltip>
													<Tooltip title="Delete row">
														<IconButton
															size="small"
															onClick={() =>
																removeRow(rIdx)
															}
														>
															<i
																className="fa-light fa-trash"
																style={{
																	fontSize:
																		'12px'
																}}
															></i>
														</IconButton>
													</Tooltip>
												</>
											)}
										</Box>
									</Box>
								))}
								<Box display="flex" alignItems="center" gap={1}>
									<Box
										width="48px"
										minWidth="48px"
										flexShrink={0}
										display="flex"
										justifyContent="center"
									>
										<Tooltip title="Append row">
											<IconButton
												size="small"
												onClick={() =>
													addRow(rowsValue.length)
												}
											>
												<i
													className="fa-solid fa-plus"
													style={{ fontSize: '10px' }}
												></i>
											</IconButton>
										</Tooltip>
									</Box>
								</Box>
							</Box>
						)}
					</Box>
				)}

				{activeTab === 1 && (
					<Box height="100%" px={2} py={0}>
						<JsonEditorDialog
							value={props.value}
							valueOwner={props.valueOwner}
							project={props.project}
							onClose={props.onClose}
							embedded={true}
							onChange={(newVal) =>
								setRowsValue(
									Array.isArray(newVal) ? newVal : []
								)
							}
						/>
					</Box>
				)}
			</Box>

			<div
				style={{
					padding: '20px 40px',
					borderTop: '1px solid var(--border-color)',
					display: 'flex',
					justifyContent: 'flex-end',
					gap: '12px'
				}}
			>
				<Button
					style={{
						background: 'transparent',
						color: 'var(--text-color)'
					}}
					onClick={(e: any) => props.onClose?.(e)}
				>
					Cancel
				</Button>
				<Button onClick={handleSave}>Save value</Button>
			</div>

			<Menu
				anchorEl={typeMenuAnchor}
				open={Boolean(typeMenuAnchor)}
				onClose={() => {
					setTypeMenuAnchor(null);
					setActiveCellForType(null);
				}}
			>
				<MenuItem
					onClick={() => {
						if (activeCellForType)
							updateCell(
								activeCellForType.rIdx,
								activeCellForType.cIdx,
								''
							);
						setTypeMenuAnchor(null);
					}}
				>
					<i className="fa-solid fa-font" style={{ width: 24 }}></i>{' '}
					String
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (activeCellForType)
							updateCell(
								activeCellForType.rIdx,
								activeCellForType.cIdx,
								0
							);
						setTypeMenuAnchor(null);
					}}
				>
					<i
						className="fa-solid fa-hashtag"
						style={{ width: 24 }}
					></i>{' '}
					Number
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (activeCellForType)
							updateCell(
								activeCellForType.rIdx,
								activeCellForType.cIdx,
								false
							);
						setTypeMenuAnchor(null);
					}}
				>
					<i
						className="fa-solid fa-toggle-on"
						style={{ width: 24 }}
					></i>{' '}
					Boolean
				</MenuItem>
				<MenuItem
					onClick={() => {
						if (activeCellForType)
							updateCell(
								activeCellForType.rIdx,
								activeCellForType.cIdx,
								null
							);
						setTypeMenuAnchor(null);
					}}
				>
					<i className="fa-solid fa-ban" style={{ width: 24 }}></i>{' '}
					Empty
				</MenuItem>
			</Menu>

			{cloneDialogState.open && (
				<SmallDialog
					onClose={(e: any) =>
						setCloneDialogState({ open: false, rowIndex: null })
					}
				>
					<Box display="flex" flexDirection="column" gap={2} p={2}>
						<Typography
							variant="h6"
							fontWeight="bold"
							display="flex"
							alignItems="center"
							gap={1}
						>
							<i className="fa-regular fa-copy"></i> Clone Row
						</Typography>
						<Typography>
							How many duplicates do you want to create?
						</Typography>
						<TextField
							type="number"
							size="small"
							value={cloneCount}
							onChange={(e) =>
								setCloneCount(parseInt(e.target.value) || 0)
							}
							autoFocus
							onKeyDown={(e) => {
								if (e.key === 'Enter') handleCloneConfirm();
							}}
						/>
						<Box
							display="flex"
							justifyContent="flex-end"
							gap={1}
							mt={2}
						>
							<Button
								style={{
									background: 'transparent',
									color: 'var(--text-color)'
								}}
								onClick={() =>
									setCloneDialogState({
										open: false,
										rowIndex: null
									})
								}
							>
								Cancel
							</Button>
							<Button onClick={handleCloneConfirm}>Clone</Button>
						</Box>
					</Box>
				</SmallDialog>
			)}
		</div>
	);
};

export default SpreadsheetRowsBuilderDialog;
